import { v4 as uuidv4 } from 'uuid';

const ZONE_KEY = 'uwpActionScope';

// Work still running this long after its action started no longer belongs to it (stops self-rescheduling timers).
export const MAX_ACTION_AGE_MS = 60_000;

interface ActionScope {
    id: string;
    start: number;
    owner: any;
    ended: boolean;
}

function getZone(): any {
    return (globalThis as any).Zone;
}

function runAsAction<T>(actionId: string | undefined, fn: () => T): T {
    const zone = getZone();
    if (!zone || !actionId) {
        return fn();
    }

    const scope: ActionScope = { id: actionId, start: Date.now(), owner: zone.currentTask, ended: false };

    return zone.current.fork({ name: 'uwp-action', properties: { [ZONE_KEY]: scope } }).run(fn);
}

/**
 * The user action that the running code belongs to (sent as X-Action-Id).
 *
 * An action exists only inside the callback passed to `run` / `resume`: it forks a Zone that carries the id, so
 * everything the callback does, including the async work it schedules, belongs to that action. Nothing outside the
 * callback can see it or change it.
 */
export const ActionContext = {
    // The id of the action the running code belongs to, if any.
    get(): string | undefined {
        const zone = getZone();
        const scope: ActionScope | undefined = zone?.current.get(ZONE_KEY);
        if (!scope || scope.ended || Date.now() - scope.start > MAX_ACTION_AGE_MS) {
            return undefined;
        }

        // Listeners and timers registered while an action ran are invoked on behalf of whoever triggers them.
        // The exception is the action's own request completing: that XHR callback is still the action's work.
        const task = zone.currentTask;
        const isOwnRequest = task?.source?.startsWith('XMLHttpRequest.');
        if (task && task !== scope.owner && !isOwnRequest && (task.type === 'eventTask' || task.data?.isPeriodic)) {
            return undefined;
        }

        return scope.id;
    },

    // Runs fn as part of the current action, or as a new action when none is running.
    run<T>(fn: () => T): T {
        return ActionContext.get() ? fn() : runAsAction(uuidv4(), fn);
    },

    // Runs fn as the existing action with this id (e.g. the id on a server push), even inside another action.
    // With no id fn runs outside any action.
    resume<T>(actionId: string | undefined, fn: () => T): T {
        return runAsAction(actionId, fn);
    },

    // Ends the current action: calls made afterwards (e.g. by the page we navigated to) are not attributed to it.
    end(): void {
        const scope: ActionScope | undefined = getZone()?.current.get(ZONE_KEY);
        if (scope) {
            scope.ended = true;
        }
    }
};
